import React, { useEffect, useState } from 'react';
import { Layout } from 'antd';
import { QuizCategory } from 'src/data/models/quiz/QuizCategoryModel';
import generateQuiz from 'src/utils/generateQuiz';
import QuizCategorySelectionScreen from './quiz/QuizCategorySelection';
import QuizContents from './quiz/QuizContents';
import { useDarkMode } from 'src/context/DarkModeContext';
import QuizResultScreen from './quiz/QuizResults';
import { QuizQuestion } from 'src/data/models/quiz/QuizQuestionModel';
import { QuizQuestionAnswer } from 'src/data/models/quiz/QuizQuestionResultModel';

export enum QuizPageState {
  SELECTION = "SELECTION",
  PLAYING = "PLAYING",
  RESULTS = "RESULTS",
}

export interface StateToScreenMapping {
  [key: string]: () => React.ReactNode;
}

const Quiz: React.FC = () => {
  const [selectedCategories, setSelectedCategories] = useState<QuizCategory[]>([]);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [quizAnswers, setQuizAnswers] = useState<QuizQuestionAnswer[]>([]);
  const [quizState, setQuizState] = useState<QuizPageState>(QuizPageState.SELECTION);
  const { isDarkMode } = useDarkMode();

  useEffect(() => {
    const generated = generateQuiz(selectedCategories);
    setQuizQuestions(generated);
  }, [selectedCategories, generateQuiz]);

  const handleCategoryChange = (checkedValues: QuizCategory[]) => {
    setSelectedCategories(checkedValues);
  };

  const startNewQuiz = () => {
    setQuizAnswers([]);
    setQuizQuestions([]);
    setSelectedCategories([]);
    setQuizState(QuizPageState.SELECTION);
  };

  const playQuiz = () => {
    setQuizState(QuizPageState.PLAYING);
  };

  const endQuiz = () => {
    setQuizState(QuizPageState.RESULTS);
  };

  const recordAnswer = (answer: QuizQuestionAnswer) => {
    setQuizAnswers((prev) => [...prev, answer]);
  };

  const renderCategorySelectionScreen = (): React.ReactNode => {
    return (
      <QuizCategorySelectionScreen
        playQuiz={playQuiz}
        selectedCategories={selectedCategories}
        onCategorySelectionChange={handleCategoryChange}
      />
    );
  }

  const renderQuizContentsScreen = (): React.ReactNode => {
    return (
      <QuizContents
        quizQuestions={quizQuestions}
        recordAnswer={recordAnswer}
        endQuiz={endQuiz}
      />
    )
  }

  const renderQuizResultsScreen = (): React.ReactNode => {
    return (
      <QuizResultScreen
        quizAnswers={quizAnswers}
        startNewQuiz={startNewQuiz}
      />
    );
  }

  const stateToScreenMapping: StateToScreenMapping = {
    [QuizPageState.SELECTION]: renderCategorySelectionScreen,
    [QuizPageState.PLAYING]: renderQuizContentsScreen,
    [QuizPageState.RESULTS]: renderQuizResultsScreen,
  };

  const renderCurrentScreen = (): React.ReactNode => {
    const renderFunction = stateToScreenMapping[quizState];
    if (renderFunction) {
      return renderFunction();
    }
    throw new Error("Unexpected error occur, please retry later"); // Handle potential unknown states
  };

  return (
    <Layout id='quiz-main-container' className={`${isDarkMode ? "dark" : ""}`} style={{ height: '100%' }}>
      {renderCurrentScreen()}
    </Layout>
  );
};

export default Quiz;
